import type { ApiMaintenanceEvent } from '@/api/equipmentApi'
import type { MaintenanceTask, TaskStatus } from '@/types/maintenanceTask'

/**
 * The single definition of a maintenance event's displayed status.
 *
 * Lifted out of `useMyList` so the mobile surface can reuse it: importing the
 * hook itself would drag `@tanstack/react-query`, `useMock` and the demo
 * fixtures in `src/mocks/` into the `/m` bundle, and re-implementing "overdue"
 * over there would give the two surfaces two definitions that quietly drift.
 *
 * Pure and React-free on purpose, so both callers and their tests can use it.
 */

/**
 * Today as an ISO date, UTC.
 *
 * Only the "Due today" count reads it now. Overdue no longer depends on it: the
 * server decides overdue in the tenant's timezone (z8nrz7dyc7).
 */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

/**
 * The worklist status of a maintenance event: its lifecycle, with a
 * not-started event that is overdue shown as `overdue`.
 *
 * Overdue itself comes from the server (`ev.overdue`), which derives it from
 * the due date, the tenant's timezone and its grace, never from status. A
 * STARTED event stays `started` here even when it is overdue, and carries its
 * own `overdue` flag instead (see `apiEventToTask`). That keeps the actions tied
 * to the real lifecycle (a started task offers End, not Start) and keeps the
 * default sort order unchanged: overdue work is marked, not moved.
 */
export function deriveStatus(ev: ApiMaintenanceEvent): TaskStatus {
  if (ev.status === 'COMPLETED') return 'completed'
  if (ev.status === 'STARTED') return 'started'
  return ev.overdue ? 'overdue' : 'due'
}

/**
 * A row after an optimistic Start / End / Undo End, until the refetch lands.
 *
 * Completing a task ends "overdue" (a completed task is never overdue), so the
 * red marker leaves with the status rather than lingering until the refetch.
 * Undoing a completion drops the completed-late marker the same way; whether
 * the reopened task is overdue again is the server's call on the refetch.
 * Starting a task changes nothing about overdue.
 */
export function withStatusOverride(task: MaintenanceTask, status: TaskStatus): MaintenanceTask {
  if (status === 'completed') return { ...task, status, overdue: false, daysOverdue: null }
  return { ...task, status, completedLate: false }
}

/**
 * The overdue marker's text, with its age: "Overdue · 5 days". The age counts
 * from the due date, not from the end of any grace, so a task due July 15 with
 * two days' grace reads 5 days on July 20 (z8nrz7dyc7 AC7).
 */
export function overdueLabel(daysOverdue: number | null | undefined): string {
  if (!daysOverdue || daysOverdue < 1) return 'Overdue'
  return `Overdue · ${daysOverdue} ${daysOverdue === 1 ? 'day' : 'days'}`
}
